import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import "./HomeHero.css";
import { FaMapMarkerAlt } from "react-icons/fa";
import dayjs from "dayjs";
import { useHomeContext } from "../../../context/Home_context";
import { buildYouTubeBackgroundEmbedUrl } from "../../../utils/helper";

const ShowLogo = ({ show }) => {
  const imgSrc = show.image_full_path || "";
  const name = show.name || "";
  const link = show.link || null;

  if (!imgSrc) return null;

  const content = <img src={imgSrc} alt={name} loading="lazy" />;

  if (link) {
    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="hh-show-card"
      >
        {content}
      </a>
    );
  }

  return <div className="hh-show-card">{content}</div>;
};

const splitIntoTwoLines = (text) => {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= 3) return [text];

  // Aim for a punchy 2nd line (3 to 4 words) so the white badge fits text tightly
  let splitIndex = Math.max(1, words.length - 4);

  for (let i = Math.max(1, words.length - 5); i < words.length - 2; i++) {
    if (/['’]s$|^(of|for|in|and|&|the|to|a)$/i.test(words[i])) {
      splitIndex = i + 1;
      break;
    }
  }

  const line1 = words.slice(0, splitIndex).join(" ");
  const line2 = words.slice(splitIndex).join(" ");

  return [line1, line2].filter(Boolean);
};

export const formatExpoTitle = (rawTitle) => {
  if (!rawTitle) return null;

  const normalized = String(rawTitle).trim();

  let lines = [];
  if (normalized.includes("\n")) {
    lines = normalized
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  } else {
    lines = splitIntoTwoLines(normalized);
  }

  if (lines.length === 0) return null;

  if (lines.length === 1) {
    return <span className="hh-slogan-line">{lines[0]}</span>;
  }

  return (
    <>
      {lines.map((line, index) => {
        const isSecondRow = index === 1;
        return (
          <span
            key={index}
            className={isSecondRow ? "hh-highlight-badge" : "hh-slogan-line"}
          >
            {line}
          </span>
        );
      })}
    </>
  );
};

const HomeHero = () => {
  const { current_expo_data } = useHomeContext();

  const youtubeEmbedUrl = buildYouTubeBackgroundEmbedUrl(
    current_expo_data?.unlock_sucess_link,
  );

  // Format dates dynamically using dayjs
  const expoDates = useMemo(() => {
    const from =
      current_expo_data?.from_date && dayjs(current_expo_data.from_date);
    const to = current_expo_data?.to_date && dayjs(current_expo_data.to_date);

    if (!from?.isValid() || !to?.isValid()) return null;

    return {
      fromDay: from.format("DD"),
      toDay: to.format("DD"),
      isRange: !from.isSame(to, "day"),
      isSameMonth: from.isSame(to, "month"),
      fromMonth: from.format("MMMM").toUpperCase(),
      toMonth: to.format("MMMM").toUpperCase(),
      fromYear: from.format("YYYY"),
      toYear: to.format("YYYY"),
    };
  }, [current_expo_data?.from_date, current_expo_data?.to_date]);

  const marqueeItems = useMemo(() => {
    const gallery = current_expo_data?.gallerys;
    if (!gallery?.length) return [];

    const repeatCount = Math.ceil(12 / gallery.length);
    return Array.from({ length: repeatCount }, () => gallery).flat();
  }, [current_expo_data?.gallerys]);

  return (
    <section className="hh-hero">
      {/* ---------- Video background (dynamic YouTube from API) ---------- */}
      <div className="hh-video-wrap" aria-hidden="true">
        {youtubeEmbedUrl ? (
          <iframe
            className="hh-video hh-video-yt"
            src={youtubeEmbedUrl}
            title="Bharat Buildcon Background Video"
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
          />
        ) : (
          <div className="hh-video-fallback" />
        )}
        <div className="hh-video-scrim" />
      </div>

      {/* ---------- Main content ---------- */}
      <div className="hh-content">
        {/* Welcome kicker */}
        {current_expo_data?.expo_title && (
          <span className="hh-welcome-kicker">
            {current_expo_data?.expo_title}
          </span>
        )}

        {/* Hero Logo */}
        {current_expo_data?.logo_full_path && (
          <div className="hh-hero-logos-wrapper">
            <Link
              to="/"
              className="hh-hero-logo"
              aria-label={current_expo_data?.name || "Bharat Buildcon Home"}
            >
              <img
                className="hh-hero-logo-img"
                src={current_expo_data.logo_full_path}
                alt={current_expo_data?.name || "Bharat Buildcon"}
                draggable="false"
                onContextMenu={(e) => e.preventDefault()}
              />
            </Link>
          </div>
        )}

        {/* Slogan / Expo Title */}
        {current_expo_data?.expo_desc && (
          <div className="hh-slogan-box">
            <h2 className="hh-slogan-main">
              {formatExpoTitle(current_expo_data.expo_desc)}
            </h2>
          </div>
        )}

        {/* Event Dates & Location Section */}
        {(expoDates || current_expo_data?.venue_name) && (
          <div className="hh-dates">
            <div className="hh-date-block">
              {expoDates && (
                <div className="hh-date-range">
                  <span className="hh-date-box notranslate" translate="no">
                    {expoDates.fromDay}
                  </span>

                  {expoDates.isRange && !expoDates.isSameMonth && (
                    <span className="hh-date-month">{expoDates.fromMonth}</span>
                  )}

                  {expoDates.isRange && (
                    <>
                      <span className="hh-date-dash">–</span>
                      <span className="hh-date-box notranslate" translate="no">
                        {expoDates.toDay}
                      </span>
                    </>
                  )}

                  <span className="hh-date-month">
                    {expoDates.isRange && !expoDates.isSameMonth
                      ? expoDates.toMonth
                      : expoDates.fromMonth}{" "}
                    <span className="notranslate" translate="no">
                      {expoDates.toYear}
                    </span>
                  </span>
                </div>
              )}
              {current_expo_data?.venue_name &&
                (current_expo_data?.venue_link ? (
                  <a
                    href={current_expo_data.venue_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hh-date-venue"
                  >
                    <FaMapMarkerAlt className="hh-pin" />
                    {current_expo_data.venue_name}
                  </a>
                ) : (
                  <span className="hh-date-venue">
                    <FaMapMarkerAlt className="hh-pin" />
                    {current_expo_data.venue_name}
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* Action CTA Buttons - Centered at the bottom */}
        <div className="hh-cta-row hh-cta-row--center">
          <Link to="/exhibitors" className="hh-btn hh-btn--solid hh-btn--lg">
            Book your stall
          </Link>
          <Link to="/registration" className="hh-btn hh-btn--blue hh-btn--lg">
            Register as Visitor
          </Link>
        </div>
      </div>

      {/* ---------- Auto-sliding gallerys strip (Swiper React) ---------- */}
      {marqueeItems.length > 0 && (
        <div className="hh-marquee-shell">
          <div className="hh-marquee-viewport">
            <Swiper
              modules={[Autoplay, FreeMode]}
              loop={true}
              speed={6000}
              autoplay={{
                delay: 0,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              freeMode={{
                enabled: true,
                momentum: false,
              }}
              slidesPerView="auto"
              spaceBetween={22}
              allowTouchMove={true}
              className="hh-swiper-marquee"
            >
              {marqueeItems.map((show, index) => (
                <SwiperSlide
                  key={`${show.id || show.name}-${index}`}
                  className="hh-swiper-slide"
                >
                  <ShowLogo show={show} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      )}
    </section>
  );
};

export default HomeHero;
